import React, { useEffect, useId, useRef, useState } from 'react';
import { useUser } from '@clerk/react';
import PaywallOffer from './PaywallOffer';
import { useIsMobile } from '../hooks/useMediaQuery';
import { validateScanUrl } from '@privisy/ui-bridge';
import { T } from '../designTokens';
import type { Tier } from '../utils/types';

interface ScanSectionOptions {
  uiCompliance: boolean;
  trackerDetection: boolean;
  gpcValidation: boolean;
  privacyPolicy: boolean;
}

const MAX_URLS = 25;

interface ScanFormProps {
  onSubmit: (urls: string[], options: { sections: ScanSectionOptions; headless: boolean }) => void;
  isLoading: boolean;
  /** Optional credit info to show balance indicator */
  creditInfo?: {
    balance: number | null;
    tier: Tier;
    packPrice?: number;
    creditsPerPurchase?: number;
  };
  /**
   * A URL to load into the field ("Audit again" on a past site). A new nonce
   * reloads it even when the URL is the same as last time.
   */
  prefill?: { url: string; nonce: number } | null;
  /** @deprecated Use creditInfo instead */
  tierInfo?: {
    tier: Tier;
    scansThisWeek: number;
    limit: number | null;
    limitReached: boolean;
  };
}

const credits = (n: number) => `${n} credit${n === 1 ? '' : 's'}`;

/** The submit label says what the click spends, so a credit is never spent by surprise. */
export function auditButtonLabel(count: number, balance: number | null | undefined): string {
  const audits = count > 1 ? `Run ${count} audits` : 'Run audit';
  return balance === null || balance === undefined ? audits : `${audits} (${credits(Math.max(count, 1))})`;
}

export default function ScanForm({ onSubmit, isLoading, creditInfo, prefill }: ScanFormProps) {
  const isMobile = useIsMobile();
  const { user } = useUser();
  const fieldId = useId();
  const helpId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [multiMode, setMultiMode] = useState(false);
  const [url, setUrl] = useState('');
  const [multiText, setMultiText] = useState('');
  const [urlError, setUrlError] = useState<string | null>(null);
  // Unified scan entry: every URL entered here runs the full 5-stage audit,
  // including privacy-policy analysis. Running the policy stage is what lets
  // the scanner evaluate the § 1798.135(b)(1) / § 7025(g) frictionless-GPC
  // exemption for the "Do Not Sell or Share Link" check in a single pass.
  const sections: ScanSectionOptions = {
    uiCompliance: true,
    trackerDetection: true,
    gpcValidation: true,
    privacyPolicy: true,
  };
  const headless = true;

  useEffect(() => {
    if (!prefill) return;
    setMultiMode(false);
    setUrl(prefill.url);
    setUrlError(null);
    inputRef.current?.focus();
  }, [prefill]);

  // Parse multi-URL textarea: one URL per non-empty line, capped at MAX_URLS
  const parsedUrls = multiText
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .slice(0, MAX_URLS);

  const isOverLimit = multiText.split('\n').map((l) => l.trim()).filter(Boolean).length > MAX_URLS;
  const balance = creditInfo?.balance;
  const auditCount = multiMode ? parsedUrls.length : 1;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    if (multiMode) {
      if (parsedUrls.length === 0) {
        setUrlError('Enter at least one website address, one per line.');
        return;
      }
      // ScanUrlValidation is a discriminated union on `ok` — TS only lets us
      // read `.normalized`/`.error` after narrowing on `.ok` directly at each
      // access site, so the checks below can't be hoisted through .find()/.map()
      // without re-checking `.ok` inline.
      const validations = parsedUrls.map((raw) => ({ raw, result: validateScanUrl(raw) }));
      const firstInvalid = validations.find((v) => !v.result.ok);
      if (firstInvalid && !firstInvalid.result.ok) {
        setUrlError(`${firstInvalid.raw}: ${firstInvalid.result.error}`);
        return;
      }
      // A batch is charged all at once or not at all (#1589), so a batch
      // larger than the balance would only come back as a refusal.
      if (typeof balance === 'number' && parsedUrls.length > balance) {
        setUrlError(
          `${parsedUrls.length} audits need ${credits(parsedUrls.length)} and you have ${credits(balance)}. Remove ${parsedUrls.length - balance} or add credits on the Billing page.`
        );
        return;
      }
      setUrlError(null);
      onSubmit(
        validations.map((v) => (v.result.ok ? v.result.normalized : v.raw)),
        { sections, headless }
      );
    } else {
      // An empty submit goes through validation like any other input, so the
      // button never first-paints disabled-gray.
      const validation = validateScanUrl(url.trim());
      if (!validation.ok) {
        setUrlError(validation.error);
        return;
      }
      setUrlError(null);
      const normalized = validation.normalized;
      setUrl(normalized);
      onSubmit([normalized], { sections, headless });
    }
  };

  // Issue #971: at a zero balance the scanner used to stack three buy CTAs in
  // three treatments — the header's cobalt nav chip, a red-tinted "No scan
  // credits remaining" strip here, and (after a submit) PaywallOffer's panel.
  // The red strip is the one that had to go: it framed a purchase decision as
  // a user error directly above the panel arguing the opposite (#943).
  //
  // The offer *replaces* the form rather than sitting above it. A zero
  // balance means every submit is a guaranteed 402, so leaving the form up
  // only invites the user to earn a second copy of this same panel from
  // ScannerDashboardContent — the duplication the unification was meant to
  // end. The header chip stays: it is persistent navigation, not a claim
  // about a mistake.
  //
  // `balance === null` (unlimited) and an unresolved fetch both skip this —
  // see useCreditInfo, which reports null for an account it could not read.
  const outOfCredits =
    creditInfo != null && creditInfo.balance !== null && creditInfo.balance <= 0;

  // A running audit at a zero balance is usually the free locked audit the
  // claim starts right after sign-up (#1767). "You're out of credits" above
  // it would compete with the unlock offer the finished report carries, so
  // the offer waits until no audit is running. A paid audit that just spent
  // the last credit gets the same treatment, which reads better there too.
  if (outOfCredits && isLoading) {
    return (
      <p role="status" style={{ margin: 0, color: T.gray300, fontSize: '15px', lineHeight: 1.5 }}>
        Your audit is running. The report opens here when it finishes.
      </p>
    );
  }

  if (outOfCredits) {
    return (
      <PaywallOffer
        state="out-of-credits"
        context="scan-entry"
        userId={user?.id}
        packPrice={creditInfo.packPrice}
        creditsPerPurchase={creditInfo.creditsPerPurchase}
      />
    );
  }

  const fieldStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '0 16px',
    minHeight: '48px',
    fontSize: '16px',
    border: `1px solid ${urlError ? T.red300 : T.gray300}`,
    borderRadius: '8px',
    backgroundColor: T.white,
    color: T.gray800,
  };

  const modeButton = (mode: 'single' | 'multi', label: string) => {
    const active = (mode === 'multi') === multiMode;
    return (
      <button
        type="button"
        aria-pressed={active}
        disabled={isLoading}
        onClick={() => {
          setMultiMode(mode === 'multi');
          setUrlError(null);
        }}
        className="scan-mode"
        style={{
          minHeight: isMobile ? '44px' : '32px',
          padding: '0 12px',
          fontSize: '13px',
          fontWeight: active ? 600 : 500,
          color: active ? T.gray900 : T.gray300,
          backgroundColor: active ? T.gray100 : 'transparent',
          border: 'none',
          borderRadius: '6px',
          cursor: isLoading ? 'not-allowed' : 'pointer',
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <form onSubmit={handleSubmit} noValidate style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
        <label htmlFor={fieldId} style={{ fontSize: '14px', fontWeight: 600, color: T.gray200 }}>
          {multiMode ? `Website addresses, one per line (up to ${MAX_URLS})` : 'Website address'}
        </label>
        <div
          role="group"
          aria-label="How many sites to audit"
          style={{ display: 'inline-flex', gap: '2px', padding: '2px', borderRadius: '8px', backgroundColor: T.gray800 }}
        >
          {modeButton('single', 'One site')}
          {modeButton('multi', 'Several sites')}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: '12px',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: multiMode && !isMobile ? 'flex-end' : 'stretch',
        }}
      >
        {multiMode ? (
          <div style={{ flex: 1, position: 'relative' }}>
            <textarea
              id={fieldId}
              className="scan-input"
              value={multiText}
              onChange={(e) => {
                setMultiText(e.target.value);
                if (urlError) setUrlError(null);
              }}
              placeholder={'example.com\nshop.example.com\nblog.example.com'}
              disabled={isLoading}
              rows={6}
              aria-invalid={!!urlError}
              aria-describedby={helpId}
              style={{
                ...fieldStyle,
                padding: '12px 16px 36px',
                fontFamily: T.fontMono,
                fontSize: '14px',
                lineHeight: 1.6,
                resize: 'vertical',
              }}
              onBlur={() => {
                if (parsedUrls.length > 0) {
                  const firstInvalid = parsedUrls
                    .map((raw) => ({ raw, result: validateScanUrl(raw) }))
                    .find((v) => !v.result.ok);
                  setUrlError(
                    firstInvalid && !firstInvalid.result.ok
                      ? `${firstInvalid.raw}: ${firstInvalid.result.error}`
                      : null
                  );
                }
              }}
            />
            <span
              aria-live="polite"
              style={{
                position: 'absolute',
                bottom: '10px',
                right: '12px',
                fontSize: '12px',
                fontWeight: 600,
                fontFamily: T.fontMono,
                fontVariantNumeric: 'tabular-nums',
                color: isOverLimit ? T.red700 : T.gray600,
                pointerEvents: 'none',
              }}
            >
              {parsedUrls.length}/{MAX_URLS}
              {isOverLimit && `, first ${MAX_URLS} used`}
            </span>
          </div>
        ) : (
          <input
            ref={inputRef}
            id={fieldId}
            className="scan-input"
            type="text"
            inputMode="url"
            autoComplete="url"
            spellCheck={false}
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              if (urlError) setUrlError(null);
            }}
            placeholder="example.com"
            disabled={isLoading}
            aria-invalid={!!urlError}
            aria-describedby={helpId}
            style={{ ...fieldStyle, flex: 1, minWidth: 0 }}
            onBlur={() => {
              if (url.trim()) {
                const validation = validateScanUrl(url);
                setUrlError(validation.ok ? null : validation.error);
              }
            }}
          />
        )}
        <button
          type="submit"
          disabled={isLoading}
          className="scan-submit"
          style={{
            minHeight: '48px',
            padding: '0 24px',
            fontSize: '16px',
            fontWeight: 600,
            color: T.white,
            backgroundColor: isLoading ? T.gray700 : T.cobalt600,
            border: 'none',
            borderRadius: '8px',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            whiteSpace: 'nowrap',
            width: isMobile ? '100%' : undefined,
          }}
        >
          {isLoading ? 'Running audit…' : auditButtonLabel(auditCount, balance)}
        </button>
      </div>

      {urlError && (
        <p
          role="alert"
          style={{ fontSize: '14px', lineHeight: 1.5, color: T.red300, margin: '10px 0 0' }}
        >
          {urlError}
        </p>
      )}

      <p
        id={helpId}
        style={{
          margin: '12px 0 0',
          fontSize: '13px',
          fontFamily: T.fontMono,
          fontVariantNumeric: 'tabular-nums',
          color: T.gray400,
        }}
      >
        {typeof balance === 'number' ? `${credits(balance)} left · ` : ''}most audits finish in under 2 minutes
      </p>
    </form>
  );
}
